/**
 * CreateOrgDialog — a minimal hand-rolled modal for spinning up a new tenant.
 *
 * Better Auth requires a unique slug per org, so we derive one from the name and
 * tack on a short random suffix to avoid collisions; if the server still reports
 * a slug clash we retry once with a fresh suffix. On success we make the new org
 * active, clear the (tenant-scoped, un-namespaced) react-query cache so nothing
 * from the previous tenant leaks through, and drop the user into onboarding.
 *
 * There is no shared Dialog primitive in this repo, so the overlay/card and its
 * dismiss behaviour (backdrop click, Escape, Cancel) are built inline.
 */
import { useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authClient } from '@/lib/auth-client';
import { queryClient } from '@/lib/query-client';

/**
 * A URL-safe slug from the name plus a random suffix for uniqueness.
 *
 * The suffix is drawn from `crypto.getRandomValues`, not `Math.random()`
 * (security review, finding 5). Since the per-org MCP URLs plan, a slug is not
 * just a uniqueness token — it is the tenant identifier in
 * `https://…/mcp/{slug}`, and `packages/db`'s `opaqueWorkspaceSlug` was moved
 * to a CSPRNG for exactly this reason: "a predictable PRNG would hand back
 * what it buys". That reasoning was never applied here, on the path where the
 * slug is *already* half-known from the organisation name — so guessing it
 * meant guessing only the suffix, and V8's xorshift128+ is not a barrier to
 * that.
 *
 * 10 chars of base36 (~51 bits), matching the auto-created slug's width, drawn
 * with rejection sampling so the alphabet stays uniform. This is a client-side
 * default and NOT a server-side constraint — `organization.create` accepts
 * whatever slug it is sent — so treat it as raising the floor, not as
 * enforcement. FU7 is what makes it a rule.
 */
const SLUG_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
const SLUG_SUFFIX_CHARS = 10;
/** 252 = 36 × 7: bytes at or above this would bias the first four letters. */
const SLUG_UNBIASED_CEILING = 252;

function randomSlugSuffix(): string {
  let out = '';
  while (out.length < SLUG_SUFFIX_CHARS) {
    for (const byte of crypto.getRandomValues(new Uint8Array(SLUG_SUFFIX_CHARS))) {
      if (byte >= SLUG_UNBIASED_CEILING) continue;
      out += SLUG_ALPHABET[byte % SLUG_ALPHABET.length];
      if (out.length === SLUG_SUFFIX_CHARS) break;
    }
  }
  return out;
}

function makeSlug(name: string): string {
  const base = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${base}-${randomSlugSuffix()}`;
}

/** Whether a create error looks like a slug/name uniqueness clash worth retrying. */
function isSlugConflict(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const code = String((error as { code?: unknown }).code ?? '').toUpperCase();
  const message = String((error as { message?: unknown }).message ?? '').toLowerCase();
  return (
    code.includes('SLUG') ||
    code.includes('ALREADY_EXISTS') ||
    code.includes('ALREADY_TAKEN') ||
    message.includes('slug') ||
    (message.includes('already') && message.includes('exist'))
  );
}

export function CreateOrgDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Start each opening from a clean field.
  useEffect(() => {
    if (open) setName('');
  }, [open]);

  // Escape dismisses — but never mid-submit.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      let result = await authClient.organization.create({ name: trimmed, slug: makeSlug(trimmed) });
      // The suffix is random, so a slug clash almost always clears on one retry.
      if (result.error && isSlugConflict(result.error)) {
        result = await authClient.organization.create({ name: trimmed, slug: makeSlug(trimmed) });
      }
      if (result.error || !result.data) {
        toast.error(result.error?.message ?? 'Could not create workspace');
        return;
      }
      const organizationId = result.data.id;
      await authClient.organization.setActive({ organizationId });
      // Cache is tenant-scoped but not keyed by tenant — clear it so nothing from
      // the previous org bleeds into the fresh one.
      queryClient.clear();
      onClose();
      void navigate({ to: '/onboarding' });
      toast.success('Workspace created');
    } catch {
      toast.error('Could not create workspace');
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click-to-dismiss; Escape and the Cancel button are the keyboard paths
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-org-title"
      onClick={(e) => {
        // Dismiss only when the backdrop itself is clicked, not when the click
        // bubbles up from inside the card — so no handler is needed on the card.
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-background p-6 shadow-lg">
        <h2 id="create-org-title" className="text-base font-semibold">
          Create workspace
        </h2>
        <p className="mt-1 text-[13.5px] text-muted-foreground">
          A workspace keeps its own chats, keys, and members, separate from your others.
        </p>
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="mt-4 space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="workspace-name">Workspace name</Label>
            <Input
              id="workspace-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Inc."
              autoFocus
              disabled={submitting}
              maxLength={120}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={submitting || !name.trim()}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Creating…
                </>
              ) : (
                'Create workspace'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
